import Hook from "../utils/hook"
import { dragScroll, dragScrollActions } from "../utils/drag-scroll"
import { keyDirection } from "../utils/key-direction"

export default Hook(`Slideshow`, {
  refs: [`baseSlideshow`, `modalSlideshow`],
  values: { activeIndex: Number },
  targets: [`navPrev`, `navNext`, `slides`, `item`],
  actions: [
    {
      name: `navPrevClick`,
      target: `navPrev`,
      type: `click`,
    },
    {
      name: `navNextClick`,
      target: `navNext`,
      type: `click`,
    },
    {
      name: `slidesScroll`,
      target: `slides`,
      type: `scroll`,
      passive: true,
      throttle: 500,
    },
    {
      name: `modalShow`,
      type: `slideshow:modal-show`,
    },
    {
      name: `modalHide`,
      type: `slideshow:modal-hide`,
    },
    {
      name: `keyNavigate`,
      type: `keydown`,
    },
    ...dragScrollActions(`slides`),
  ],

  ...dragScroll,

  modalKeydownHandler: null,

  mounted() {
    this.slidesTarget.scrollLeft = 0
    this.toggleNav()

    // rendered into an already open modal at a given slide (e.g. a preview),
    // so its `slideshow:modal-show` came before this hook existed
    if (this.activeIndexValue != null) {
      this.modalKeyNavigationStart()
      this.showIndex(this.activeIndexValue)
    }
  },

  destroyed() {
    this.modalKeyNavigationStop()
  },

  navPrevClickAction() {
    this.scrollToDirection(`prev`)
  },

  navNextClickAction() {
    this.scrollToDirection(`next`)
  },

  slidesScrollAction() {
    this.toggleNav()
  },

  modalShowAction() {
    this.modalKeyNavigationStart()

    if (this.baseSlideshowRef)
      this.showIndex(() => this.baseSlideshowRef?.getCurrentIndex())
  },

  // shows the slide at `index` (or the index it returns) once the just shown
  // modal has laid out the slides
  showIndex(index) {
    window.setTimeout(() => {
      const actualIndex = typeof index === `function` ? index() : index
      if (actualIndex == null) return

      this.scrollToIndex(actualIndex)
      // the nav was last updated while the modal was hidden, when the slides
      // had no size and so seemed to be at both ends; scrolling to the first
      // slide doesn't scroll, so nothing else would update it
      this.toggleNav()
    }, 10)
  },

  modalHideAction() {
    this.modalKeyNavigationStop()

    if (this.baseSlideshowRef)
      this.baseSlideshowRef.scrollToIndex(this.getCurrentIndex())
  },

  // the slideshow in the modal takes the arrow keys while the modal is open,
  // wherever the focus is
  modalKeyNavigationStart() {
    if (this.modalKeydownHandler) return

    this.modalKeydownHandler = this.keyNavigate.bind(this)
    document.addEventListener(`keydown`, this.modalKeydownHandler)
  },

  modalKeyNavigationStop() {
    if (!this.modalKeydownHandler) return

    document.removeEventListener(`keydown`, this.modalKeydownHandler)
    this.modalKeydownHandler = null
  },

  // the inline slideshow takes the arrow keys while the focus is on or in it
  keyNavigateAction(e) {
    // the open modal's own (document) listener handles them
    if (this.modalKeydownHandler) return
    // focus can stay on the inline slideshow behind its open modal
    if (this.modalSlideshowRef?.modalKeydownHandler) return

    this.keyNavigate(e)
  },

  keyNavigate(e) {
    const direction = keyDirection(e)
    if (!direction) return

    // a focused scroll container would scroll on its own too
    e.preventDefault()
    this.scrollToDirection(direction)
  },

  dragScroller() {
    return this.slidesTarget
  },

  dragItems() {
    return this.itemTargets
  },

  dragStateClass(state) {
    return `is-${state}`
  },

  toggleNav() {
    if (this.navPrevTarget) this.navPrevTarget.disabled = this.getIsBeginning()
    if (this.navNextTarget) this.navNextTarget.disabled = this.getIsEnd()
  },

  scrollToDirection(direction) {
    const items =
      direction == `next` ? this.itemTargets : [...this.itemTargets].reverse()

    items.every((item) => {
      const listRect = this.slidesTarget.getBoundingClientRect()
      const itemRect = item.getBoundingClientRect()
      const itemOffset = itemRect.left - listRect.left
      const itemMarginLeft = Math.abs(
        parseInt(window.getComputedStyle(item).marginLeft || 0)
      )

      if (
        (direction == `next` &&
          itemRect.width - itemMarginLeft > listRect.width - itemOffset + 20) ||
        (direction == `prev` && Math.round(itemOffset) < 0)
      ) {
        item.scrollIntoView({ block: `nearest` })
        return false
      }

      return true
    })
  },

  scrollToIndex(index) {
    this.slidesTarget.style.setProperty(`scroll-behavior`, `auto`)
    this.itemTargets[index]?.scrollIntoView({
      block: `nearest`,
      behavior: `instant`,
    })
    this.slidesTarget.style.removeProperty(`scroll-behavior`)
  },

  getCurrentIndex() {
    return Math.floor(
      this.slidesTarget.scrollLeft /
        this.slidesTarget.getBoundingClientRect().width
    )
  },

  getIsBeginning() {
    return this.slidesTarget.scrollLeft < 20
  },

  getIsEnd() {
    return (
      this.slidesTarget.scrollLeft + 20 >
      this.slidesTarget.scrollWidth -
        this.slidesTarget.getBoundingClientRect().width
    )
  },
})
